import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auditsApi, exportApi } from '../api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Calendar,
  Download,
  FileText,
} from 'lucide-react';

const AUDIT_TYPES = [
  { value: 'internal', label: 'Interna' },
  { value: 'external', label: 'Externa' },
  { value: 'supplier', label: 'Fornecedor' },
  { value: 'certification', label: 'Certificação' },
  { value: 'surveillance', label: 'Manutenção' },
  { value: 'process', label: 'Processo' },
];

const MONTHS = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

const AuditSchedule = () => {
  const navigate = useNavigate();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadAudits();
  }, [year]);

  const loadAudits = async () => {
    try {
      setLoading(true);
      const res = await auditsApi.list({ limit: 100 });
      const all = res.data || [];
      setAudits(all.filter((a) => {
        const d = a.planned_date || a.start_date || a.end_date;
        if (!d) return false;
        return new Date(d).getFullYear() === year;
      }));
    } catch (error) {
      toast.error('Erro ao carregar auditorias');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const res = await exportApi.download('audit-schedule', 'pdf', { year });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `programa_auditoria_${year}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    } finally {
      setDownloading(false);
    }
  };

  const markerFor = (audit, m) => {
    const planned = audit.planned_date ? new Date(audit.planned_date) : null;
    const pm = planned ? planned.getMonth() : null;
    const hasExecutionDates = Boolean(audit.end_date || audit.start_date);
    const isRealized = audit.status === 'completed' || hasExecutionDates;
    const executed = audit.end_date || audit.start_date || audit.planned_date;
    const em = executed ? new Date(executed).getMonth() : null;

    const markers = [];
    if (pm === m) markers.push(<span key="P" className="cell-mark cell-P">P</span>);
    if (isRealized && em === m) markers.push(<span key="R" className="cell-mark cell-R">R</span>);
    if (markers.length === 0) return null;
    return <span className="cell-mark-group">{markers}</span>;
  };

  const internal = audits.filter((a) => a.audit_type === 'internal');
  const external = audits.filter((a) => a.audit_type !== 'internal');

  const TypeTable = ({ items }) => (
    <div className="card overflow-hidden schedule-table">
      <table className="table">
        <thead>
          <tr>
            <th className="schedule-name-col">Auditoria</th>
            {MONTHS.map((m) => (
              <th key={m} className="schedule-month-col">{m}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.id} className="schedule-row">
              <td className="schedule-name-cell">
                <span className="block text-white text-sm font-medium truncate">{a.title}</span>
                <span className="font-mono text-gray-400 text-xs">{a.code}</span>
              </td>
              {MONTHS.map((m, i) => (
                <td key={m} className="schedule-cell">
                  {markerFor(a, i)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="schedule-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <Link
            to="/auditorias"
            className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-green-400 transition-colors mb-2"
          >
            <ArrowLeft size={16} /> Voltar para Auditorias
          </Link>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Calendar size={24} /> Agenda Geral de Auditorias
          </h1>
          <p className="text-gray-400 text-sm">Programa anual de auditorias internas e externas</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            className="input px-3 w-32"
          >
            {[year - 1, year, year + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button onClick={handleDownload} disabled={downloading} className="btn-primary flex items-center gap-2" data-testid="schedule-download-btn">
            {downloading ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
            PDF
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-sm text-gray-300">
        <span className="flex items-center gap-2"><span className="cell-mark cell-P">P</span> Planejada</span>
        <span className="flex items-center gap-2"><span className="cell-mark cell-R">R</span> Realizada</span>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-24">
          <Loader2 className="animate-spin text-green-500" size={32} />
        </div>
      ) : (
        <>
          {/* Internal section */}
          <section>
            <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <FileText size={18} /> Auditorias Internas
            </h2>
            {internal.length === 0 ? (
              <p className="text-gray-500 text-sm">Nenhuma auditoria interna para {year}.</p>
            ) : (
              <TypeTable items={internal} />
            )}
          </section>

          {/* External section */}
          <section>
            <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <FileText size={18} /> Auditorias Externas
            </h2>
            {external.length === 0 ? (
              <p className="text-gray-500 text-sm">Nenhuma auditoria externa para {year}.</p>
            ) : (
              <TypeTable items={external} />
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default AuditSchedule;